import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Share2, Reply, Users, Rss, ThumbsUp, ThumbsDown, RefreshCw, Folder as FolderIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MessageDisplay } from '@/components/MessageDisplay';
import FollowupChips from '@/components/FollowupChips';
import BookmarkOrganizePopover from '@/components/BookmarkOrganizePopover';
import { extractFollowupChips } from '@/utils/extractFollowupChips';
import ThinkingIndicator from './ThinkingIndicator';
import SourceChips from './SourceChips';

/**
 * Renders a single chat message bubble with all action buttons.
 * Handles both user and assistant messages.
 */
export default function ChatMessageBubble({
  msg,
  idx,
  loading,
  copiedMessageId,
  setCopiedMessageId,
  feedbackMap,
  feedbackEnabled,
  bookmarkedIds,
  bookmarkMeta,
  bookmarkGroupsById,
  bookmarkLabelsById,
  isGuest,
  sources,
  onCopy,
  onFeedback,
  onBookmarkAdded,
  onBookmarkRemoved,
  onBookmarkMetaChange,
  onGroupCreated,
  onLabelCreated,
  conversationId,
  onShare,
  onShareToFriend,
  onPostToFeed,
  onReply,
  onRetry,
  onScriptureClick,
  onGuestBlock,
  onSourceChipClick,
  onFollowupSelect,
  isStreaming,
  textareaRef,
}) {
  const { t } = useTranslation();

  if (msg.role === 'user') {
    return (
      <div
        className="bg-[var(--theme-accentLight)] theme-text rounded-3xl rounded-tr-sm px-6 py-4 shadow-sm"
      >
        {msg.replyTo && (
          <div className="mb-2 pb-2 border-b border-white/10 flex items-start gap-2">
            <div className="w-0.5 h-full min-h-[20px] rounded-full flex-shrink-0" style={{ background: 'var(--theme-accent)', opacity: 0.5 }} />
            <p className="text-xs leading-relaxed opacity-60 line-clamp-2">{msg.replyTo.content}</p>
          </div>
        )}
        <div className="message-content text-base leading-relaxed whitespace-pre-wrap">
          {msg.content}
        </div>
        <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 hover:bg-white/10 rounded-full"
            onClick={() => {
              navigator.clipboard.writeText(msg.content);
              setCopiedMessageId('user-' + idx);
              setTimeout(() => setCopiedMessageId(null), 2000);
            }}
            title={t('chat.copy_tooltip')}
            data-testid={`copy-user-msg-${idx}`}
          >
            {copiedMessageId === 'user-' + idx ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5 theme-text-secondary" />
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div
      className="theme-surface border theme-border theme-text rounded-3xl rounded-tl-sm px-6 py-4 shadow-sm"
    >
      {loading && !msg.content && !msg.timedOut && !msg.error ? (
        <ThinkingIndicator />
      ) : (
        <>
          <MessageDisplay content={msg.content} onScriptureClick={onScriptureClick} />

          {/* Actions row */}
          {msg.content && !msg.timedOut && !msg.error && (
            <div className="mt-4 pt-3 border-t theme-border flex items-center gap-2">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                onClick={() => { onReply(msg); if (textareaRef?.current) textareaRef.current.focus(); }}
                title={t('chat_messages.reply_tooltip')} data-testid={`reply-msg-${idx}`}
              >
                <Reply className="h-4 w-4 theme-text-secondary" />
              </Button>

              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                onClick={() => isGuest ? onGuestBlock('share conversations') : onShare(msg)}
                title={t('chat.share_tooltip')} data-testid={`share-msg-${idx}`}
                style={isGuest ? { opacity: 0.4 } : undefined}
              >
                <Share2 className="h-4 w-4 theme-text-secondary" />
              </Button>

              {!isGuest && onShareToFriend && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                  onClick={() => onShareToFriend(msg)} title={t('chat_messages.share_friend_tooltip')} data-testid={`share-friend-msg-${idx}`}
                >
                  <Users className="h-4 w-4 theme-text-secondary" />
                </Button>
              )}

              {!isGuest && onPostToFeed && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                  onClick={() => onPostToFeed(msg)} title={t('chat_messages.post_feed_tooltip')} data-testid={`post-feed-msg-${idx}`}
                >
                  <Rss className="h-4 w-4 theme-text-secondary" />
                </Button>
              )}

              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                onClick={() => onCopy(msg)} title={t('chat.copy_tooltip')} data-testid={`copy-msg-${idx}`}
              >
                {copiedMessageId === msg.message_id ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 theme-text-secondary" />
                )}
              </Button>

              <BookmarkOrganizePopover
                msg={msg}
                isBookmarked={!isGuest && bookmarkedIds.has(msg.message_id)}
                isGuest={isGuest}
                onGuestBlock={onGuestBlock}
                onBookmarkAdded={onBookmarkAdded}
                onBookmarkRemoved={onBookmarkRemoved}
                onBookmarkMetaChange={onBookmarkMetaChange}
                onGroupCreated={onGroupCreated}
                onLabelCreated={onLabelCreated}
                groupsById={bookmarkGroupsById}
                labelsById={bookmarkLabelsById}
                meta={bookmarkMeta}
                conversationId={conversationId}
                testIdSuffix={idx}
              />

              {feedbackEnabled && (
                <>
                  <div className="w-px h-4 bg-[var(--glass-border)] mx-1" />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                    onClick={() => onFeedback(msg, 'up')} data-testid={`feedback-up-${idx}`}
                  >
                    <ThumbsUp className={`h-4 w-4 ${feedbackMap[msg.message_id] === 'up' ? 'text-green-500' : 'theme-text-secondary'}`} />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-[var(--theme-hover)] rounded-full"
                    onClick={() => onFeedback(msg, 'down')} data-testid={`feedback-down-${idx}`}
                  >
                    <ThumbsDown className={`h-4 w-4 ${feedbackMap[msg.message_id] === 'down' ? 'text-red-500' : 'theme-text-secondary'}`} />
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Inline bookmark organisation chips — only when this message
              is bookmarked AND has a group OR labels. Tiny, one-line,
              nestled under the actions row. Click-through opens the
              popover via a synthetic click on the bookmark trigger. */}
          {!isGuest && msg.content && !msg.timedOut && !msg.error && bookmarkedIds.has(msg.message_id) && bookmarkMeta && (
            (bookmarkMeta.group_name || (bookmarkMeta.labels && bookmarkMeta.labels.length > 0)) && (
              <div
                className="mt-2 flex items-center flex-wrap gap-1.5"
                data-testid={`bookmark-chips-${idx}`}
              >
                {bookmarkMeta.group_name && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide"
                    style={{
                      background: 'color-mix(in srgb, var(--theme-accent) 14%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--theme-accent) 30%, transparent)',
                      color: 'var(--theme-accent)',
                    }}
                    data-testid={`bookmark-chip-group-${idx}`}
                  >
                    <FolderIcon className="h-2.5 w-2.5" />
                    {bookmarkMeta.group_name}
                  </span>
                )}
                {(bookmarkMeta.labels || []).map((lbl) => (
                  <span
                    key={lbl.label_id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                    style={{
                      background: `color-mix(in srgb, ${lbl.color} 16%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${lbl.color} 35%, transparent)`,
                      color: lbl.color,
                    }}
                    data-testid={`bookmark-chip-label-${lbl.label_id}-${idx}`}
                  >
                    <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: lbl.color }} />
                    {lbl.name}
                  </span>
                ))}
              </div>
            )
          )}

          {/* Source citation chips */}
          {msg.content && !msg.timedOut && !msg.error && sources && sources.length > 0 && (
            <SourceChips sources={sources} onChipClick={(source, chipIdx) => onSourceChipClick(msg.message_id, chipIdx)} />
          )}

          {/* Follow-up suggestion chips (only on the most recent assistant
              message, only after streaming has completed). The parent
              decides eligibility by passing `onFollowupSelect` only for the
              latest message. */}
          {msg.content && !msg.timedOut && !msg.error && onFollowupSelect && (() => {
            const { chips } = extractFollowupChips(msg.content);
            if (!chips || chips.length === 0) return null;
            return (
              <FollowupChips
                chips={chips}
                onSelect={onFollowupSelect}
                disabled={!!isStreaming}
              />
            );
          })()}
        </>
      )}

      {msg.error && msg.retryContent && (
        <button
          onClick={() => onRetry(msg.retryContent)}
          className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80"
          style={{ background: 'var(--theme-accent)', color: '#fff' }}
          data-testid={`retry-msg-${idx}`}
        >
          {t('chat_messages.retry')}
        </button>
      )}

      {msg.timedOut && msg.retryContent && (
        <div className="mt-3 pt-3 border-t theme-border">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm" style={{ color: 'var(--theme-accent)' }}>
              {msg.content ? t('chat_messages.response_interrupted') : t('chat_messages.unable_to_generate')}
            </span>
            <button
              onClick={() => onRetry(msg.retryContent)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105 active:scale-95"
              style={{ background: 'var(--theme-accent)', color: '#fff' }}
              data-testid={`retry-timeout-${idx}`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t('chat_messages.try_again')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
